import { useEffect, useRef, useState } from "react";

interface AdSlotProps {
  /** Unique per-page id — used for Raptive reporting. */
  id: string;
  /** Reserve space to avoid CLS; Raptive collapses if unfilled. */
  minHeight?: number;
  className?: string;
  /** Optional label shown above the slot when an ad renders. */
  label?: string;
}

/**
 * Raptive/AdThrive manual placement slot.
 *
 * Raptive's manual ads plugin scans the DOM for `div.adthrive-ad` and fills
 * them with a suitable creative. When the plugin isn't loaded (admin routes,
 * preview, ad blockers) the slot simply stays empty — no layout jump because
 * we reserve `minHeight`.
 */
export default function AdSlot({ id, minHeight = 250, className = "", label = "Advertisement" }: AdSlotProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as any;
    const requestFill = () => {
      if (!w.adthrive || !w.adthrive.cmd) return;
      w.adthrive.cmd.push(() => {
        try {
          w.adthrive?.reloadAds?.();
        } catch {
          /* not ready */
        }
      });
    };
    requestFill();
    const retries = [1500, 4000].map((t) => window.setTimeout(requestFill, t));

    // Poll for a real ad creative. Only then reveal the wrapper — otherwise
    // the slot stays fully collapsed so users never see a blank rectangle.
    let observer: MutationObserver | null = null;
    const check = () => {
      const el = ref.current;
      if (!el) return false;
      const hit = el.querySelector("iframe, img, ins, [id^='google_ads']");
      if (hit) {
        setFilled(true);
        return true;
      }
      return false;
    };
    if (ref.current) {
      observer = new MutationObserver(() => { if (check()) observer?.disconnect(); });
      observer.observe(ref.current, { childList: true, subtree: true });
    }
    const stopTimer = window.setTimeout(() => observer?.disconnect(), 8000);

    return () => {
      retries.forEach(clearTimeout);
      clearTimeout(stopTimer);
      observer?.disconnect();
    };
  }, [id]);

  return (
    <div
      ref={wrapRef}
      className={`w-full justify-center print:hidden ad-slot-wrapper ${filled ? "flex my-4 md:my-6" : ""} ${className}`}
      aria-label={label}
      style={filled ? undefined : { height: 0, overflow: "hidden", margin: 0 }}
    >
      <div
        ref={ref}
        id={`adthrive-${id}`}
        className="adthrive-ad adthrive-content w-full max-w-[970px] mx-auto"
        data-google-query-id={id}
        data-cy="adthrive-ad"
        style={{ minHeight: filled ? minHeight : 0, display: "block" }}
      />
    </div>
  );
}